/* eslint-disable no-param-reassign */
/**
 * homeAppetitePdf — client-side PDF for the Home page "View Risk Appetite" button (jsPDF + autotable).
 *
 * Three sections, all driven by live config:
 *   p1  Risk appetite settings   — the full L1→L2 taxonomy, each L2's appetite cell filled with its
 *                                  risk-appetite level colour (Settings › Appetite Settings › Risk
 *                                  Appetite Levels).
 *   p2  Risk appetite definitions — compliance statement + a level × criteria grid (appetite levels
 *                                  as coloured columns, criteria rows e.g. Definition, Control env…).
 *   p3+ Risk category definitions — per L1 (name + definition) then each L2 (name + definition), from
 *                                  the risk-taxonomy config.
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { PAGER_LOGO } from "./pagerLogo";

// PAGER brand palette (chrome only — taxonomy + appetite-level colours stay config-driven).
const BRAND = [36, 64, 142]; // PAGER navy #24408E
const BRAND_ORANGE = [214, 124, 40]; // #D67C28
const PAGER_WEB = "pagerisk.com";
const PAGER_EMAIL = "info@pagerisk.com";
const DEFAULT_COMPLIANCE =
  "Compliance with all company policies is mandatory; none of the following compromises this obligation.";

const hexToRgb = (hex) => {
  const h = String(hex || "")
    .replace("#", "")
    .trim();
  if (h.length !== 6) return [120, 120, 130];
  const n = parseInt(h, 16);
  // eslint-disable-next-line no-bitwise
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

// Pick a readable text colour (dark on light fills like yellow, white on dark fills like red/green).
const textOn = (rgb) => {
  const [r, g, b] = rgb;
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? [28, 28, 28] : [255, 255, 255];
};

// Equal fixed widths for the matrix's L1 columns (1..n), so the grid lines up edge to edge.
const buildColW = (n, w) => {
  const o = {};
  for (let i = 1; i <= n; i += 1) o[i] = { cellWidth: w };
  return o;
};

// ID139 — legacy/variant appetite value → the default level-title bucket (so "Opportunity" matches
// the configured level "Opportunity Seeking"). Keeps home + PDF appetite colouring consistent.
const APPETITE_TITLE_ALIAS = {
  "risk aversion": "risk aversion",
  "risk averse": "risk aversion",
  "balanced risk aversion": "balanced risk aversion",
  "risk balanced": "balanced risk aversion",
  "balanced opportunity": "balanced opportunity",
  opportunity: "opportunity seeking",
  "opportunity seeking": "opportunity seeking",
  "risk seeking": "opportunity seeking",
};

// Resolve an L2's appetite string to its configured level {title,color}. Matches on title (case/space
// insensitive), then via the legacy alias; returns null when unset or unmatched (cell uncoloured).
const levelFor = (appetite, levels) => {
  const a = String(appetite || "")
    .trim()
    .toLowerCase();
  if (!a) return null;
  const arr = levels || [];
  const byTitle = (t) =>
    arr.find(
      (l) =>
        String(l.title || "")
          .trim()
          .toLowerCase() === t
    );
  return byTitle(a) || (APPETITE_TITLE_ALIAS[a] ? byTitle(APPETITE_TITLE_ALIAS[a]) : null) || null;
};

export function generateAppetitePdf({ categories = [], appetite = {}, companyName = "" } = {}) {
  const levels = Array.isArray(appetite.levels) ? appetite.levels : [];
  const criteria = Array.isArray(appetite.criteria) ? appetite.criteria : [];
  const compliance = String(appetite.complianceStatement || "").trim() || DEFAULT_COMPLIANCE;

  // eslint-disable-next-line new-cap
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const M = 40;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - 2 * M;

  const title = (text, sub) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(...BRAND);
    doc.text(text, M, 44);
    if (sub) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 130);
      doc.text(sub, M, 60);
    }
  };

  // ── p1 — Risk appetite settings (taxonomy MATRIX) ────────────────────────────
  // L1 categories across the top, an "Exec Authority" (owner) row, then each category's L2 cells
  // stacked beneath it, coloured by the L2's risk-appetite level. Within a column the L2s are sorted
  // by appetite level (highest appetite first). RAS colours come from the Risk Appetite Levels config.
  title("Risk appetite settings", companyName || undefined);
  const LABEL_BG = [60, 50, 80];
  const cols = (categories || []).map((c) => {
    const l2s = (c.SubCategories || []).map((s) => {
      const lvl = levelFor(s.Appetite, levels);
      return {
        name: s.name || "",
        color: lvl ? hexToRgb(lvl.color) : null,
        order: lvl ? lvl.order || 0 : -1,
      };
    });
    l2s.sort((a, b) => b.order - a.order); // highest appetite first
    return { name: c.name || "", owner: c.OwnerName || c.owner || c.Owner || "", l2s };
  });
  const maxRows = Math.max(1, ...cols.map((c) => c.l2s.length));
  const head1 = [["Level 1", ...cols.map((c) => c.name)]];
  const body1 = [["Exec Authority", ...cols.map((c) => c.owner || "—")]];
  for (let r = 0; r < maxRows; r += 1) {
    body1.push([r === 0 ? "Level 2" : "", ...cols.map((c) => c.l2s[r]?.name || "")]);
  }
  const labelW = 54;
  const colW = cols.length ? (contentW - labelW) / cols.length : contentW;
  autoTable(doc, {
    startY: 74,
    head: head1,
    body: body1,
    margin: { left: M, right: M, bottom: 46 },
    styles: {
      fontSize: 6.3,
      cellPadding: 2.5,
      overflow: "linebreak",
      valign: "middle",
      halign: "center",
      lineColor: [255, 255, 255],
      lineWidth: 0.6,
    },
    headStyles: { fillColor: BRAND, textColor: 255, fontStyle: "bold", fontSize: 6.8 },
    columnStyles: { 0: { cellWidth: labelW, halign: "left" }, ...buildColW(cols.length, colW) },
    didParseCell: (data) => {
      const col = data.column.index;
      if (col === 0) {
        // Left label strip
        data.cell.styles.fillColor = LABEL_BG;
        data.cell.styles.textColor = 255;
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.halign = "left";
        return;
      }
      if (data.section === "body") {
        if (data.row.index === 0) {
          data.cell.styles.fillColor = [236, 235, 240];
          data.cell.styles.textColor = [60, 60, 70];
        } else {
          const cell = cols[col - 1]?.l2s[data.row.index - 1];
          if (cell && cell.color) {
            data.cell.styles.fillColor = cell.color;
            data.cell.styles.textColor = textOn(cell.color);
            data.cell.styles.fontStyle = "bold";
          }
        }
      }
    },
  });

  // ── p2 — Risk appetite definitions ───────────────────────────────────────────
  doc.addPage();
  title("Risk appetite definitions");
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 70);
  const compLines = doc.splitTextToSize(compliance, contentW);
  doc.text(compLines, M, 66);
  doc.setFont("helvetica", "normal");
  const defStartY = 66 + compLines.length * 12 + 8;
  autoTable(doc, {
    startY: defStartY,
    head: [["", ...levels.map((l) => l.title || "")]],
    body: criteria.map((cr) => [
      cr.label || "",
      ...levels.map((l) => (cr.cells || []).find((cc) => cc.levelId === l.id)?.text || ""),
    ]),
    margin: { left: M, right: M, bottom: 46 },
    styles: { fontSize: 7.5, cellPadding: 4, overflow: "linebreak", valign: "top" },
    headStyles: { textColor: 255, fontStyle: "bold" },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 92, fillColor: [245, 243, 250] } },
    didParseCell: (data) => {
      if (data.section === "head" && data.column.index >= 1) {
        const lvl = levels[data.column.index - 1];
        if (lvl) data.cell.styles.fillColor = hexToRgb(lvl.color);
      }
    },
  });

  // ── p3+ — Risk category definitions ──────────────────────────────────────────
  doc.addPage();
  title("Risk category definitions");
  let startY = 70;
  categories.forEach((c, idx) => {
    autoTable(doc, {
      startY,
      head: [
        [
          {
            content: `${c.name || "Category"}${c.desc ? ` — ${c.desc}` : ""}`,
            colSpan: 2,
            styles: { fillColor: BRAND, textColor: 255, fontStyle: "bold", fontSize: 10 },
          },
        ],
        ["Level 2 category", "Definition"],
      ],
      body: (c.SubCategories || []).length
        ? (c.SubCategories || []).map((s) => [s.name || "", s.Description || ""])
        : [["—", "(no sub-categories)"]],
      margin: { left: M, right: M, bottom: 46 },
      styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak", valign: "top" },
      headStyles: { fillColor: [120, 110, 140], textColor: 255 },
      columnStyles: {
        0: { cellWidth: contentW * 0.32, fontStyle: "bold" },
        1: { cellWidth: contentW * 0.68 },
      },
    });
    startY = doc.lastAutoTable.finalY + 16;
    if (startY > pageH - 90 && idx < categories.length - 1) {
      doc.addPage();
      startY = 50;
    }
  });

  // ── PAGER footer banner on every page (logo + web + email) ───────────────────
  const footerY = pageH - 30;
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setDrawColor(...BRAND_ORANGE);
    doc.setLineWidth(0.8);
    doc.line(M, footerY - 6, pageW - M, footerY - 6);
    try {
      // logo 362x92 (ratio 3.93) → 94 x 24
      doc.addImage(PAGER_LOGO, "PNG", M, footerY - 2, 94, 24);
    } catch (e) {
      /* logo is optional */
    }
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND);
    doc.text(`${PAGER_WEB}    ·    ${PAGER_EMAIL}`, pageW - M, footerY + 10, { align: "right" });
  }

  doc.save(`Risk-Appetite-${String(companyName || "taxonomy").replace(/\s+/g, "-")}.pdf`);
}

export default generateAppetitePdf;
